// ZONE 5 · PEOPLE — the contacts on the record, and the people the evidence names.
//
// Two populations, one zone, and the difference is visible rather than explained: a CONTACT is
// a record (solid card, sponsor flag, the engagements it sponsors); a GHOST is a name the
// timeline's participants carry that matches no contact — evidence, not a record, so its card
// is dashed exactly like every other unheld thing in this language (LAW 6).
//
// Promote writes the ghost through the existing contact-create route (POST /clients/:id/contacts),
// which is the same path the record surface uses — no second way to make a contact.
import { useState } from 'react';
import { useToast } from './Toast.js';
import * as crm from '../api/crm.js';
import { useT, useLocale } from '../i18n/index.js';
import './ClientPeople.de.js';
import './ClientPeople.fr.js';
import type { BriefContact, BriefGhost } from '../api/brief.js';

interface Props {
  clientId: number;
  contacts: BriefContact[];
  ghosts: BriefGhost[];
  /** Reload the brief — a promoted ghost stops being a ghost on the next payload. */
  onChanged: () => void;
  onOpenRecord: () => void;
}

export default function ClientPeople({ clientId, contacts, ghosts, onChanged, onOpenRecord }: Props) {
  const toast = useToast();
  const t = useT();
  const { tp, fmtDate } = useLocale();
  const [busy, setBusy] = useState<string | null>(null);

  async function promote(ghost: BriefGhost) {
    setBusy(ghost.name);
    try {
      await crm.createContact(clientId, { name: ghost.name, email: ghost.email ?? undefined });
      toast('ok', t('{name} is on the record — open the client record to give them a role.', { name: ghost.name }));
      onChanged();
    } catch (err) {
      toast('err', (err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="zone" id="people">
      <div className="zone-head">
        <span className="u-label">{t('People')}</span>
        <span className="zone-count voice-figure">
          {t('{n} on record', { n: contacts.length })}{ghosts.length ? ` · ${t('{n} named by the evidence only', { n: ghosts.length })}` : ''}
        </span>
        <button type="button" className="zone-route" onClick={onOpenRecord}>{t('Contacts & record')} →</button>
      </div>

      {!contacts.length && !ghosts.length ? (
        <p className="zone-empty">
          {t('No contact on file, and no person named on any event. G0 cannot pass on any engagement until a contact is named for it.')}
        </p>
      ) : (
        <div className="client-people">
          {contacts.map((person) => (
            <div className="client-person" key={`c${person.id}`}>
              <div className="client-person-name">
                {person.name}
                {person.is_sponsor && <span className="client-chip">{t('sponsor')}</span>}
                {(person.sponsor_of ?? []).map((code) => (
                  <span className="client-chip src-ref" key={code} title={t('Named sponsor for {code} — the answer G0 checks', { code })}>
                    {code}
                  </span>
                ))}
              </div>
              <div className="client-person-role">{person.role_title ?? <span className="n-none">{t('no role on file')}</span>}</div>
              <div className="client-person-con">
                {person.email ?? <span className="n-none">{t('no email')}</span>}
                {person.phone && <><br />{person.phone}</>}
              </div>
            </div>
          ))}

          {ghosts.map((ghost) => (
            <div className="client-person client-person--ghost" key={`g${ghost.name}-${ghost.email ?? ''}`}>
              <div className="client-person-name">
                {ghost.name}
                <span className="client-ghost-mark">◇ {t('not on the record')}</span>
              </div>
              <div className="client-person-role voice-figure">
                {tp(ghost.events, 'named on {n} event', 'named on {n} events')} ·{' '}
                {fmtDate(ghost.first_seen)} – {fmtDate(ghost.last_seen)}
              </div>
              <div className="client-person-con">{ghost.email ?? <span className="n-none">{t('no email on the event')}</span>}</div>
              <div style={{ marginTop: 'var(--s8)' }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={busy === ghost.name}
                  title={t('Create a contact from this evidence — the role can be filled in on the record')}
                  onClick={() => void promote(ghost)}
                >
                  {busy === ghost.name ? t('Promoting…') : t('Promote to contact')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
